/* eslint-disable camelcase */
/* eslint-disable security/detect-object-injection */
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { ConversationHistoryObject } from "../../components/ConversationalFlow/types";

const feedbackOptional = true;

type AgentStatus = 'pending' | 'running' | 'completed' | 'failed';

interface ManagerTask {
  task_id: number;
  name: string;
  status: AgentStatus;
  message?: string;
}

type InitialState = {
    chat_id: number,
    isActive: boolean,
    conversation: any[],
    displayRecommendedOptions: boolean,
    recommendedOptions: string[],
    lastConversationObject: any | null,
    preTrackLoading: boolean,
    kbLoading:boolean,
    preTrackError: boolean,
    kbError:boolean,
    trackApiLoading: boolean,
    aaApiLoading:boolean,
    streaming:boolean,
    uiContent:boolean,
    completion_bot:boolean,
    trackApiError: boolean,
    aaApiError: boolean
    conversationSummary:any[],
    questionIdConvLayer:any,
    topicIdConvLayer:any,
    notGivenFeedback:boolean,
    insightsToShow:any,
    isFullscreen:boolean,
    insightsAgentsLoading: boolean,
    insightsAgentsError: boolean,
    sessionId: string,
    messageId: string,
    questionId:number,
    topicId:number,
    chatTitle: string,
    isClarificationCurrentlyActive: boolean,
    activeTab: string,
    isSharedChat: boolean,
    streamingMessage:string,
    streamingAgentName: string,
    managerTasks: Record<number, ManagerTask>;
    totalTasks: number;
    completedTasks: number;
    progressPercent: number;
};

const initialState: InitialState = {
    chat_id: 0,
    isActive: true,
    conversation: [],
    displayRecommendedOptions: false,
    recommendedOptions: [],
    lastConversationObject: null,
    preTrackError: false,
    kbError: false,
    preTrackLoading: false,
    streaming: false,
    uiContent: false,
    completion_bot: false,
    kbLoading: false,
    trackApiLoading: false,
    aaApiLoading:false,
    trackApiError: false,
    aaApiError:false,
    conversationSummary: [],
    questionIdConvLayer: 0,
    topicIdConvLayer: 0,
    notGivenFeedback: !feedbackOptional,
    insightsToShow: null,
    isFullscreen: false,
    insightsAgentsLoading: false,
    insightsAgentsError: false,
    sessionId: 's1',
    messageId: '',
    topicId: 0,
    questionId: 0,
    chatTitle: '',
    isClarificationCurrentlyActive: false,
    activeTab: 'Threads',
    isSharedChat: false,
    streamingMessage:'',
    streamingAgentName:'',
    managerTasks: {},
    totalTasks: 0,
    completedTasks: 0,
    progressPercent: 0
};

export const conversationSlice = createSlice({
    name: "conversation",
    initialState,
    reducers: {

        /** 🔹 Initialize tasks from ManagerAgent */
        setManagerTasks(state, action: PayloadAction<Record<number, ManagerTask>>) {
            state.managerTasks = action.payload;
            console.log('Manager Tasks Set:', state.managerTasks);
        },

        setTotalTasks(state, action: PayloadAction<number>) {
            state.totalTasks = action.payload;
            console.log('Total Tasks Set:', state.totalTasks);
        },

        setCompletedTasks(state, action: PayloadAction<number>) {
            state.completedTasks = action.payload;
            console.log('Completed Tasks Set:', state.completedTasks);
        },

        /** Update a single agent (running / completed) */
        updateManagerTask(  state, action: PayloadAction<{ task_id: number; status: AgentStatus; message?: string; }>) {
            const { task_id, status, message } = action.payload;

            if (!state.managerTasks[task_id]) return;

            state.managerTasks[task_id].status = status;

            if (message !== undefined) {
                state.managerTasks[task_id].message = message;
            }
            console.log(`Manager Task Updated:`, state.managerTasks[task_id]);
        },

        /**  Increment completed count safely */
        incrementCompletedTasks(state) {
            state.completedTasks += 1;
        },

        /** Recalculate loader percentage */
        recalculateProgress(state) {
            if (state.totalTasks === 0) {
                state.progressPercent = 0;
                return;
            }

            state.progressPercent = Math.round(
                (state.completedTasks / state.totalTasks) * 100
            );
        },

        /**  Reset everything (on new question) */
        resetInsightsAgents(state) {
            state.managerTasks = {};
            state.totalTasks = 0;
            state.completedTasks = 0;
            state.progressPercent = 0;
        },

        setActiveTab(state, action: PayloadAction<string>) {
            state.activeTab = action.payload;
        },

        pushToConversationHistory(state, action: PayloadAction<ConversationHistoryObject>) {
            state.conversation.push(action.payload);
            state.lastConversationObject = action.payload
        },

        updateLastConversation(state, action: PayloadAction<any>){
            const lastIndex = state.conversation.length - 1;            
            if (lastIndex >= 0) {
                state.conversation[lastIndex].content.push(action.payload)                
            }
            state.lastConversationObject = state.conversation[lastIndex]
        },

        updateFeedback(state, action: PayloadAction<boolean>){
            const lastIndex = state.conversation.length - 1;
            if (lastIndex >= 0) {
                state.conversation[lastIndex].feedback=action.payload                
            }
            state.lastConversationObject = state.conversation[lastIndex]
        },

        updateLastConversationMessageId(state, action: PayloadAction<any>){
            const lastIndex = state.conversation.length - 1;
            if (lastIndex >= 0) {
                state.conversation[lastIndex].message_id = action.payload;
            }
            state.lastConversationObject = state.conversation[lastIndex]
        },

        updateLastConversationMessage(state, action: PayloadAction<any>){
            const lastIndex = state.conversation.length - 1;
            if (lastIndex >= 0) {
                state.conversation[lastIndex].content = [...state.conversation[lastIndex].content, ...action.payload];
            }
            state.lastConversationObject = state.conversation[lastIndex]
        },

        updateContent(state, action: PayloadAction<any>){
            const lastIndex = state.conversation.length - 1;
            if (lastIndex >= 0) {
                state.conversation[lastIndex].insightData = {
                    ...state.conversation[lastIndex].insightData, ...action.payload
                };
                state.conversation[lastIndex].message_id = action.payload.feedback_message_id
                state.conversation[lastIndex].question_id = action.payload.question_id
            }
            state.lastConversationObject = state.conversation[lastIndex]
        },

        setNewStreamingMessage(state,action:PayloadAction<string>){
            state.streamingMessage=action.payload
        },

        setStreamingAgentName(state,action:PayloadAction<string>){
            state.streamingAgentName=action.payload
        },

        setDisplayRecommendedOptions(state, action: PayloadAction<boolean>) {
            state.displayRecommendedOptions = action.payload;
            if (action.payload === false) {
                state.recommendedOptions = [];
            }
        },

        setQuestionId(state, action: PayloadAction<any>) {
            state.questionId = action.payload
        },

        setTopicId(state, action: PayloadAction<any>) {
            state.topicId = action.payload
        },

        setSessionId(state, action: PayloadAction<any>) {
            state.sessionId = action.payload
        },

        setMessageId(state, action: PayloadAction<any>) {
            state.messageId = action.payload
        },

        setChatTitle(state, action: PayloadAction<any>) {
            state.chatTitle = action.payload
        },

        setIsClarificationCurrentlyActive(state, action: PayloadAction<boolean>) {
            state.isClarificationCurrentlyActive = action.payload;
        },

        setIsSharedChat(state, action: PayloadAction<boolean>) {
            state.isSharedChat = action.payload;
        },
        
        setConversationHistory(state, action: PayloadAction<ConversationHistoryObject[]>) {
            state.conversation = action.payload;
            state.lastConversationObject = action.payload[action.payload.length - 1];
            console.log(state.lastConversationObject, 'lastConversationObjectinside');
            if (state.lastConversationObject?.content?.length > 0) {
                state.lastConversationObject.content.forEach((eventData: any) => {
                    switch (eventData.type) {
                        case "DBLogging":
                            console.log("DBLogging logic triggered");
                            state.topicId=eventData.db_inputs.topic_id;
                            state.questionId = eventData.db_inputs.question_id;
                            state.sessionId = eventData.db_inputs.session_id;
                            state.chatTitle = eventData.db_inputs.chat_title;
                            state.isClarificationCurrentlyActive = eventData.db_inputs.clarification;
                            break;

                        case "ClarificationLayer":
                            if (eventData.clarification_question[0].content.length > 0) {
                                const options = eventData.clarification_question[0].recommended_options ?? [];
                                state.isClarificationCurrentlyActive = true
                                if (options.length > 0) {
                                    state.displayRecommendedOptions=true
                                    state.recommendedOptions = options
                                }else {
                                    state.recommendedOptions = [];
                                    state.displayRecommendedOptions=false
                                }
                            }
                                break;
                        default:
                            // Handle or skip other types
                            break;
                    }
                });
            }
            if ((state.lastConversationObject?.recommended_options ?? []).length > 0) {
                state.displayRecommendedOptions = true;
                state.recommendedOptions = state.lastConversationObject?.recommended_options ?? [];
            } else {
                state.displayRecommendedOptions = false;
            }
            if (!feedbackOptional) {
                state.notGivenFeedback = state.lastConversationObject?.feedback
            }
        },

        setConversationSummary(state, action: PayloadAction<any>) {
            state.conversationSummary = action.payload;
        },

        setInsightsToShow(state, action: PayloadAction<any>) {
            state.insightsToShow = action.payload
        },

        setRecommendedOptions(state, action: PayloadAction<string[]>) {
            state.recommendedOptions = action.payload;
        },         
        togglePreTrackLoading(state, action: PayloadAction<boolean>) {
            state.preTrackLoading = action.payload
        },
        toggleKbLoading(state, action: PayloadAction<boolean>) {
            state.kbLoading = action.payload
        },
        togglePreTrackError(state, action: PayloadAction<boolean>) {
            state.preTrackError = action.payload
        },
        toggleKbError(state, action: PayloadAction<boolean>) {
            state.kbError = action.payload
        },
        toggleTrackApiLoading(state, action: PayloadAction<boolean>) {
            state.trackApiLoading = action.payload
        },
        toggleInsightsAgentsLoading(state, action: PayloadAction<boolean>) {
            state.insightsAgentsLoading = action.payload
        },
        toggleInsightsAgentsError(state, action: PayloadAction<boolean>) {
            state.insightsAgentsError = action.payload
        },
        toggleAaApiLoading(state, action: PayloadAction<boolean>) {
            state.aaApiLoading = action.payload
        },
        toggleStreaming(state, action: PayloadAction<boolean>) {
            state.streaming = action.payload
        },
        toggleUiContent(state, action: PayloadAction<boolean>) {
            state.uiContent = action.payload
        },
        toggleCompletionBotResponse(state, action: PayloadAction<boolean>) {
            state.completion_bot = action.payload
        },
        toggleTrackApiError(state, action: PayloadAction<boolean>) {
            state.trackApiError = action.payload
        },
        toggleAaApiError(state, action: PayloadAction<boolean>) {
            state.aaApiError = action.payload
        },
        toggleFeedback(state, action: PayloadAction<boolean>) {
            if (!feedbackOptional) {
                state.notGivenFeedback = action.payload
            }
        },
        setIsFullscreen(state, action: PayloadAction<any>) {
            state.isFullscreen = action.payload
        }
    },
});

export const {
    pushToConversationHistory,
    setDisplayRecommendedOptions,
    setRecommendedOptions,
    togglePreTrackLoading,
    toggleKbLoading,
    togglePreTrackError,
    updateContent,
    toggleFeedback,
    setIsSharedChat,
    updateLastConversation,
    updateLastConversationMessage,
    toggleKbError,
    toggleTrackApiError,
    toggleAaApiError,
    toggleTrackApiLoading,
    toggleAaApiLoading,
    setConversationHistory,
    updateFeedback,
    setConversationSummary,
    toggleStreaming,
    setIsClarificationCurrentlyActive,
    toggleCompletionBotResponse,
    setQuestionId,
    setTopicId,
    setSessionId,
    setMessageId,
    setChatTitle,
    setInsightsToShow,
    setIsFullscreen,
    toggleInsightsAgentsLoading,
    toggleInsightsAgentsError,
    updateLastConversationMessageId,
    setActiveTab,
    setNewStreamingMessage,
    toggleUiContent,
    setStreamingAgentName,
    setManagerTasks,
    setTotalTasks,
    setCompletedTasks,
    updateManagerTask,
    incrementCompletedTasks,
    recalculateProgress,
    resetInsightsAgents
} = conversationSlice.actions;

export default conversationSlice.reducer;
